
import { supabase } from '@/integrations/supabase/client';

export class DealStatusService {
  /**
   * Mark a deal as won using the edge function (avoids RLS issues)
   * This is used when authenticated customers accept proposals
   */
  static async markDealAsWon(dealId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('🎯 [DealStatusService] Starting secure deal acceptance process');
      
      // Get current authenticated user and their profile
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        console.error('❌ [DealStatusService] No authenticated user found:', userError);
        return { success: false, error: 'User must be authenticated to accept proposals' };
      }
      
      console.log('✅ [DealStatusService] Authenticated user found:', user.id);

      // Get user's email from profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('email')
        .eq('user_id', user.id)
        .single();

      if (profileError || !profile?.email) {
        console.error('❌ [DealStatusService] Could not get user email:', profileError);
        return { success: false, error: 'Could not verify user email' };
      }

      // Use edge function to mark deal as won (bypasses RLS)
      const { data, error } = await supabase.functions.invoke('proposal-accept', {
        body: { 
          dealId, 
          contactEmail: profile.email 
        }
      });

      if (error) {
        console.error('❌ [DealStatusService] Edge function failed:', error);
        return { success: false, error: `Failed to mark deal as won: ${error.message}` };
      }

      if (!data?.success) {
        console.error('❌ [DealStatusService] Edge function returned failure:', data);
        return { success: false, error: data?.error || 'Deal not found or could not be updated' };
      }

      // Update any existing orders for this deal with the customer user_id
      // The trigger will have created the order, but we need to ensure customer_user_id is set
      console.log('🔄 [DealStatusService] Updating orders with customer user_id...');
      
      // First get the sales that belong to this deal
      const { data: sales, error: salesError } = await supabase
        .from('sales')
        .select('id')
        .eq('deal_id', dealId);

      if (salesError) {
        console.warn('⚠️ [DealStatusService] Failed to fetch sales for deal:', salesError);
      } else if (sales && sales.length > 0) {
        // Update orders for each sale - set customer_user_id to the authenticated user
        const saleIds = sales.map(sale => sale.id);
        
        const { error: orderUpdateError } = await supabase
          .from('orders')
          .update({ customer_user_id: user.id })
          .in('sale_id', saleIds)
          .is('customer_user_id', null); // Only update if not already set

        if (orderUpdateError) {
          console.warn('⚠️ [DealStatusService] Failed to update orders with customer_user_id:', orderUpdateError);
          // Don't fail the entire operation for this
        } else {
          console.log('✅ [DealStatusService] Orders updated with customer user_id');
        }
      }

      console.log('🎉 [DealStatusService] Deal marked as won successfully');
      return { success: true };

    } catch (error: any) {
      console.error('💥 [DealStatusService] Unexpected error:', error);
      return { success: false, error: `Unexpected error: ${error.message}` };
    }
  }

  /**
   * Verify that a deal is properly marked as won with consistent data
   */
  static async verifyWonDealStatus(dealId: string): Promise<{ isProperlyWon: boolean; dealInfo?: any }> {
    try {
      // Add 500ms delay to allow database trigger to complete
      console.log('⏱️ [DealStatusService] Waiting 500ms for database trigger to complete...');
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const { data: deal, error } = await supabase
        .from('crm_deals')
        .select('id, status, stage_id, probability, proposal_accepted_at')
        .eq('id', dealId)
        .single();

      if (error || !deal) {
        console.error('❌ [DealStatusService] Deal verification failed:', error);
        return { isProperlyWon: false };
      }

      const isProperlyWon = (
        deal.status === 'won' &&
        deal.stage_id === null &&
        deal.probability === 100 &&
        deal.proposal_accepted_at !== null
      );

      console.log('🔍 [DealStatusService] Deal verification result:', {
        dealId,
        isProperlyWon,
        status: deal.status,
        stage_id: deal.stage_id,
        probability: deal.probability,
        accepted_at: deal.proposal_accepted_at
      });

      return { isProperlyWon, dealInfo: deal };
    } catch (error) {
      console.error('💥 [DealStatusService] Verification error:', error);
      return { isProperlyWon: false };
    }
  }
}
